import createSagaMiddleware, { EffectMiddleware } from '@redux-saga/core';
import { configureStore } from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import { AnyAction, ReducersMapObject, combineReducers } from 'redux';
import { PersistedState, persistReducer, persistStore } from 'redux-persist';
import { environment } from '../configuration/env';
import * as sagaInjector from './injectSaga';
import { rootPersistConfig, setPersistor } from './persistence';
import staticRootReducer from './rootReducer';
import rootSaga from './rootSaga';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type State = any;

const effectMiddleware = (next => effect => {
  if (effect) {
    batch(() => next(effect));
    return;
  }

  next(effect);
}) as EffectMiddleware;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createRootReducer(allReducers: ReducersMapObject<any, any>) {
  const persistedReducer = persistReducer(rootPersistConfig, combineReducers(allReducers));

  function rootReducer(state: object & State & PersistedState, action: AnyAction) {
    return persistedReducer(state, action);
  }

  return rootReducer;
}

const rootReducer = createRootReducer(staticRootReducer);

const configureOnboardingStore = () => {
  const sagaMiddleware = createSagaMiddleware({
    effectMiddlewares: [effectMiddleware],
  });

  // set runner for lazy loaded sagas
  sagaInjector.setSagaRunner(sagaMiddleware.run);

  const middlewares = [sagaMiddleware];

  const store = configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware => {
      return getDefaultMiddleware({
        serializableCheck: false,
      }).concat(middlewares);
    },
    devTools: !environment.PROD,
  });

  const persistor = persistStore(store);
  setPersistor(persistor);

  const sagas = [rootSaga];
  sagas.forEach(saga => {
    sagaMiddleware.run(saga);
  });

  return { store, persistor };
};

export default configureOnboardingStore;
